import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import { PLATFORMS } from "../../constants"
import AudienceController from "./controllers/AudienceController"
import SegmentsSelector from "./components/SegmentsSelector"
import FilteringControls from "./components/FilteringControls"
import NewsletterAPI from "../../../api"

const AudienceStep = ({ formData, errors, onInputChange }) => {
  const [availableSegments, setAvailableSegments] = useState([])
  const [loadingSegments, setLoadingSegments] = useState(true)
  const [systemLanguages, setSystemLanguages] = useState([])
  const [loadingLanguages, setLoadingLanguages] = useState(true)
  const [basePlatformBreakdown, setBasePlatformBreakdown] = useState(null)
  const [baseAudienceTotal, setBaseAudienceTotal] = useState(0)
  const [baseLanguageBreakdown, setBaseLanguageBreakdown] = useState(null)
  const [releases, setReleases] = useState([])
  const [loadingReleases, setLoadingReleases] = useState(true)

  useEffect(() => {
    loadSegments()
    loadSystemLanguages()
    loadReleases()
  }, [])

  // Load base breakdown (without platform/language filters)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.segments.length > 0) {
        loadBaseBreakdown()
      } else {
        setBasePlatformBreakdown(null)
        setBaseLanguageBreakdown(null)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [formData.segments, formData.includeUnregistered, formData.ltvFilterEnabled, formData.ltvAmount, formData.ltvOperator])


  const loadSegments = async () => {
    setLoadingSegments(true)
    const response = await AudienceController.loadSegments()
    setLoadingSegments(false)

    if (response.success) {
      setAvailableSegments(response.segments)

      if (response.segments.length > 0 && (!formData.segments || formData.segments.length === 0)) {
        const defaultSegment = AudienceController.findDefaultSegment(response.segments)
        if (defaultSegment) {
          onInputChange("segments", [defaultSegment.id])
        }
      }
    }
  }

  const loadSystemLanguages = async () => {
    setLoadingLanguages(true)
    const response = await AudienceController.loadSystemLanguages()
    setLoadingLanguages(false)

    setSystemLanguages(response.languages)

    if (formData.targetLanguages.length === 0) {
      onInputChange("targetLanguages", ["any"])
    }
  }

  const loadReleases = async () => {
    setLoadingReleases(true)
    try {
      const response = await NewsletterAPI.getReleases({
        status: ['running', 'completed', 'failed'],
        limit: 100
      })

      if (response && response.success && response.releases) {
        const releaseOptions = response.releases.map(release => {
          // Format the date if available
          let dateStr = ''
          if (release.createdAt) {
            const date = new Date(release.createdAt)
            dateStr = ` (${date.toLocaleDateString()})`
          }

          return {
            value: release._id,
            label: `${release.name}${dateStr}`
          }
        })
        setReleases(releaseOptions)
      }
    } catch (error) {
      console.error('Failed to load releases:', error)
      setReleases([])
    } finally {
      setLoadingReleases(false)
    }
  }

  const loadBaseBreakdown = async () => {
    const response = await AudienceController.previewAudience(
      formData.segments,
      formData.includeUnregistered,
      [], // No language filter for base breakdown
      [], // No platform filter for base breakdown
      formData.ltvFilterEnabled ? formData.ltvAmount : null,
      formData.ltvFilterEnabled ? formData.ltvOperator : 'gte',
      formData.deliveryFilterEnabled ? formData.deliveryOperator : null,
      formData.deliveryFilterEnabled ? formData.deliveryReleases : [],
      100 // Full audience
    )

    if (response.success) {
      setBasePlatformBreakdown(response.preview?.breakdown?.platforms)
      setBaseLanguageBreakdown(response.preview?.breakdown?.languages)
      setBaseAudienceTotal(response.preview?.breakdown?.total || 0)
    }
  }


  const handleSegmentChange = (selectedSegments) => {
    // Single selection - receives array with one item or empty array
    if (!selectedSegments || selectedSegments.length === 0) {
      // Fallback to "all-subscribers" if cleared
      const allSubscribersSegment = availableSegments.find(seg => seg.id === 'all-subscribers')
      if (allSubscribersSegment) {
        onInputChange("segments", ['all-subscribers'])
        return
      }
    }

    onInputChange("segments", [selectedSegments[0].id])
  }

  const getSelectedSegments = () => {
    // Return single segment for single selection
    const segment = availableSegments.find(segment => formData.segments.includes(segment.id))
    return segment || null
  }

  const handleLanguageChange = (selectedLanguages) => {
    const languageCodes = selectedLanguages.map(lang => lang.value)
    const processedCodes = AudienceController.handleAnyExclusivity(languageCodes, formData.targetLanguages || [])
    onInputChange("targetLanguages", processedCodes)
  }

  const handlePlatformChange = (selectedPlatforms) => {
    const platformCodes = selectedPlatforms.map(platform => platform.value)
    const processedCodes = AudienceController.handleAnyExclusivity(platformCodes, formData.targetPlatforms || [])
    onInputChange("targetPlatforms", processedCodes)
  }

  const getSelectedLanguages = () => {
    return systemLanguages.filter(lang => formData.targetLanguages.includes(lang.value))
  }

  const getSelectedPlatforms = () => {
    return PLATFORMS.filter(platform => formData.targetPlatforms.includes(platform.value))
  }

  const handleDeliveryReleasesChange = (selectedReleases) => {
    const releaseIds = selectedReleases.map(release => release.value)
    onInputChange("deliveryReleases", releaseIds)
  }

  const getSelectedReleases = () => {
    return releases.filter(release => formData.deliveryReleases.includes(release.value))
  }

  return (
    <div className="space-y-4">
      {errors.general && (
        <div className="bg-admin_error/10 rounded-lg border border-admin_error p-4 mb-4">
          <Typography variant="admin_txt2" className="text-admin_error">
            {errors.general}
          </Typography>
        </div>
      )}

      <SegmentsSelector
        availableSegments={availableSegments}
        selectedSegments={getSelectedSegments()}
        loadingSegments={loadingSegments}
        error={errors.segments}
        onChange={handleSegmentChange}
      />

      <FilteringControls
        formData={formData}
        errors={errors}
        systemLanguages={systemLanguages}
        platforms={PLATFORMS}
        loadingLanguages={loadingLanguages}
        basePlatformBreakdown={basePlatformBreakdown}
        baseLanguageBreakdown={baseLanguageBreakdown}
        baseAudienceTotal={baseAudienceTotal}
        releases={releases}
        loadingReleases={loadingReleases}
        onInputChange={onInputChange}
        onLanguageChange={handleLanguageChange}
        onPlatformChange={handlePlatformChange}
        onDeliveryReleasesChange={handleDeliveryReleasesChange}
        getSelectedLanguages={getSelectedLanguages}
        getSelectedPlatforms={getSelectedPlatforms}
        getSelectedReleases={getSelectedReleases}
      />
    </div>
  )
}

export default AudienceStep
